import { useMemo } from 'react';
import { useSpacesQuery } from './useSpacesQuery';
import { useInfiniteMemoriesQuery } from './useInfiniteMemoriesQuery';

interface UseWorkspaceLoadingOptions {
  spaceId?: string | null;
  enabled?: boolean;
}

interface UseWorkspaceLoadingResult {
  isLoading: boolean;
  isSpacesLoading: boolean;
  isMemoriesLoading: boolean;
  error: Error | null;
  spacesError: Error | null;
  memoriesError: Error | null;
}

/**
 * Coordinates loading states between spaces and the first page of memories.
 */
export function useWorkspaceLoading(options: UseWorkspaceLoadingOptions = {}): UseWorkspaceLoadingResult {
  const { spaceId, enabled = true } = options;

  const spacesQuery = useSpacesQuery();
  const memoriesQuery = useInfiniteMemoriesQuery({
    spaceId: spaceId || undefined,
    enabled: enabled && !!spaceId,
  });

  const result = useMemo(() => {
    const isLoading =
      spacesQuery.isLoading || (spaceId ? memoriesQuery.isLoading : false);
    const error = spacesQuery.error || memoriesQuery.error || null;

    return {
      isLoading,
      isSpacesLoading: spacesQuery.isLoading,
      isMemoriesLoading: memoriesQuery.isLoading,
      error,
      spacesError: spacesQuery.error,
      memoriesError: memoriesQuery.error,
    };
  }, [
    spacesQuery.isLoading,
    spacesQuery.error,
    memoriesQuery.isLoading,
    memoriesQuery.error,
    spaceId,
  ]);

  return result;
}
